// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import 'promise-polyfill/src/polyfill';
import 'whatwg-fetch';

import styled, { injectGlobal } from 'styled-components';
import {
    createElement,
    Component,
    Grid,
    GridCol,
    LocaleProvider,
    PropTypes,
    Toaster,
    Translate,
} from '@plesk/ui-library';
import Bytes from './Bytes';
import Disk from './Disk';
import Memory from './Memory';
import Overview from './Overview';
import Services from './Services';

import locale from './locale/en-US.json';

let apiToken = null;
const baseFetch = (url, init) => fetch(url, { credentials: 'same-origin', ...init });
const api = (url, init = {}) => {
    const request = () => {
        const headers = {};

        if (apiToken) {
            headers['X-Authorization'] = apiToken;
        }

        return baseFetch(`/repair/backend${url}`, { ...init, headers: { ...headers, ...init.headers } });
    };

    return request()
        .then(response => {
            if (response.status === 401) {
                if (url !== '/token') {
                    return api('/token', { method: 'post' })
                        .then(({ token }) => {
                            apiToken = token;
                            return request();
                        });
                }
                window.location.reload();
            }

            return response;
        })
        .then(response => {
            if (response.ok) {
                return response.json();
            }

            if (response.status === 500) {
                return response.json()
                    .then(({ message }) => {
                        const error = new Error(message);
                        error.response = response;
                        throw error;
                    });
            }

            const error = new Error(response.statusText);
            error.response = response;
            throw error;
        });
};
const waitSuccessResponse = (request, timeout = 3000) => new Promise(resolve => {
    const executeRequest = () => {
        setTimeout(() => {
            request().then(response => {
                if (response.ok) {
                    resolve(response);
                } else {
                    executeRequest();
                }
            }).catch(executeRequest);
        }, timeout);
    };

    executeRequest();
});

injectGlobal`
    .pul-media__figure { margin-right: 10px; } /* TODO UILIB-133 Right margin of icons in Media */
    .pul-grid--gap-md { margin-bottom: -16px; } /* TODO UILIB-136 Bottom extra spacing in Grid component into any visual block */
`;

const Container = styled.div``;
const Header = styled.header`
    display: flex;
    align-items: center;
    height: 50px;
    padding: 0 20px;
    background-color: #222a32;
`;
const Logo = styled.div``;
const Main = styled.div`
    padding: 0 20px;
`;
const Content = styled.div`
    max-width: 960px;
    margin: auto;
    padding: 20px 0;
`;
const Title = styled.h1`
    margin: 0 0 16px;
    padding: 0;
    font-size: 24px;
    font-weight: 400;
    line-height: 28px;
`;

class App extends Component {
    state = {
        data: null,
        cleaningDisk: false,
        cleaningMemory: false,
        restartingServices: false,
    };

    componentDidMount() {
        const serviceIds = ['PleskSQLServer', 'SwEngine'];

        api('/token', { method: 'post' }).then(({ token }) => {
            apiToken = token;
            Promise.all([
                api('/disk')
                    .then(this.mapDiskData)
                    .catch(e => Promise.resolve({ error: e.message })),
                api('/system-resources')
                    .then(this.mapMemoryData)
                    .catch(e => Promise.resolve({ error: e.message })),
                Promise.all(serviceIds.map(id => api(`/services/${id}`)
                    .catch(() => Promise.resolve(null))))
                    .then(services =>
                        services
                            .map((service, index) => service && { id: serviceIds[index], ...service })
                            .filter(service => service)
                    ),
            ])
                .then(([disk, memory, services]) => {
                    this.setState({
                        data: { disk, memory, services },
                    });
                });
        });
    }

    mapMemoryData = ({ memory, swap }) => [{
        type: 'RAM',
        requires: Math.max(this.calculatePercent(5, memory.total), 104857600),
        ...memory,
    }, {
        type: 'Swap',
        requires: this.calculatePercent(5, swap.total),
        ...swap,
    }];

    mapDiskData = disk => disk.map(item => ({
        requires: this.calculatePercent(5, item.total),
        ...item,
    }));

    calculatePercent(percent, value) {
        return Math.round(value * percent / 100);
    }

    calculateFreeSize(data) {
        return data.reduce((res, { free }) => res + free, 0);
    }

    handleDiskCleanUp = () => {
        this.toaster.clear();
        this.setState({
            cleaningDisk: true,
        });

        api('/disk', {
            method: 'post',
        })
            .then(disk => {
                const cleanSize = this.calculateFreeSize(disk) - this.calculateFreeSize(this.state.data.disk);

                this.setState({
                    data: {
                        ...this.state.data,
                        disk: this.mapDiskData(disk),
                    },
                    cleaningDisk: false,
                });

                this.toaster.add({
                    intent: cleanSize > 0 ? 'success' : 'info',
                    message: cleanSize > 0 ? (
                        <Translate
                            content="App.diskCleanUpSuccess"
                            params={{
                                size: <Bytes>{cleanSize}</Bytes>,
                            }}
                        />
                    ) : (
                        <Translate
                            content="App.diskCleanUpNothing"
                        />
                    ),
                });
            })
            .catch(error => {
                this.setState({
                    cleaningDisk: false,
                });

                this.catchApiError(error);
            });
    };

    handleMemoryCleanUp = () => {
        this.toaster.clear();
        this.setState({
            cleaningMemory: true,
        });

        api('/system-resources', {
            method: 'post',
        })
            .then(resources => {
                const memory = this.mapMemoryData(resources);
                const cleanSize = this.calculateFreeSize(memory) - this.calculateFreeSize(this.state.data.memory);

                this.setState({
                    data: {
                        ...this.state.data,
                        memory,
                    },
                    cleaningMemory: false,
                });

                this.toaster.add({
                    intent: cleanSize > 0 ? 'success' : 'info',
                    message: cleanSize > 0 ? (
                        <Translate
                            content="App.memoryCleanUpSuccess"
                            params={{
                                size: <Bytes>{cleanSize}</Bytes>,
                            }}
                        />
                    ) : (
                        <Translate
                            content="App.memoryCleanUpNothing"
                        />
                    ),
                });
            })
            .catch(error => {
                this.setState({
                    cleaningMemory: false,
                });

                this.catchApiError(error);
            });
    };

    handleRestartServices = () => {
        this.toaster.clear();
        this.setState({
            restartingServices: true,
        });

        const serviceIds = this.state.data.services.map(({ id }) => id);
        Promise.all(serviceIds.map(id => api(`/services/${id}`, { method: 'post' })))
            .then(services => {
                this.setState({
                    data: {
                        ...this.state.data,
                        services: services
                            .map((service, index) => ({ id: serviceIds[index], ...service })),
                    },
                    restartingServices: false,
                });

                this.toaster.add({
                    intent: 'success',
                    message: (
                        <Translate
                            content="App.restartServisesSuccess"
                        />
                    ),
                });
            })
            .catch(error => {
                this.setState({
                    restartingServices: false,
                });

                this.catchApiError(error);
            });
    };

    handleRestartServer = () => {
        this.toaster.clear();
        const waitAndRedirect = () => new Promise(resolve => {
            setTimeout(() => {
                resolve(waitSuccessResponse(
                    () => baseFetch(window.location, { method: 'head' })
                ));
            }, 30000);
        })
            .then(() => {
                this.toaster.add({
                    intent: 'success',
                    message: (
                        <Translate
                            content="App.rebootServerSuccess"
                        />
                    ),
                });
            });

        return api('/reboot', { method: 'post' })
            .then(waitAndRedirect)
            .catch(this.catchApiError);
    };

    catchApiError = ({ message }) => {
        this.toaster.add({
            intent: 'danger',
            message,
        });
    };

    render() {
        const { data } = this.state;

        if (!data) {
            return null;
        }

        return (
            <LocaleProvider messages={locale}>
                <Container>
                    <Header>
                        <Logo><img src="images/plesk.svg" height="27" alt="Plesk" /></Logo>
                    </Header>
                    <Main>
                        <Content>
                            <Title><Translate content="App.title" /></Title>
                            <Grid gap="md">
                                <GridCol sm={12}>
                                    <Overview onRestartServer={this.handleRestartServer} />
                                </GridCol>
                                <GridCol sm={12}>
                                    <Disk data={data.disk} onCleanUp={this.handleDiskCleanUp} isProcessing={this.state.cleaningDisk} />
                                </GridCol>
                                <GridCol sm={6}>
                                    <Memory data={data.memory} onCleanUp={this.handleMemoryCleanUp} isProcessing={this.state.cleaningMemory} />
                                </GridCol>
                                <GridCol sm={6}>
                                    <Services data={data.services} onRestart={this.handleRestartServices} isProcessing={this.state.restartingServices} />
                                </GridCol>
                            </Grid>
                        </Content>
                    </Main>
                    <Toaster ref={ref => (this.toaster = ref)} />
                </Container>
            </LocaleProvider>
        );
    }
}

export default App;



// WEBPACK FOOTER //
// ./app/repair/App.js